'use client';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { useState, useEffect, useCallback } from 'react';
import Section from '@repo/ui/src/components/Section';
import SearchAutocomplete from '@components/shared/SearchAutocomplete';
import { searchSubmitted } from '@repo/ui/src/services/analytics';
import { MetaProps } from '@repo/ui/src/types/meta';
import Seo, { Props as SeoProps } from '@repo/ui/src/components/Seo';
import SearchResultsList from './_SearchResultsList';
import Tabs from '@repo/ui/src/components/Tabs';

interface TabProps {
  tabName: string;
  searchType: string;
  redirectUrl?: string;
}

interface Props {
  _meta: MetaProps;
  title: string;
  searchPlaceholder: string;
  body?: any;
  tabLabel: string;
  pageTabs: {
    tabs: Array<TabProps>;
    _meta: MetaProps;
  };
  resultTextSingular: string;
  resultTextPlural: string;
  slotContent: Array<SeoProps>;
}

const SearchResultsBody = ({
  title,
  searchPlaceholder,
  tabLabel,
  pageTabs,
  resultTextSingular,
  resultTextPlural,
  slotContent,
}: Props) => {
  const tabs = pageTabs ? pageTabs.tabs : [];
  const { query } = useCurrentUrl();

  const searchTerm = query?.get('term') ?? '';
  const page = query.get('page') ?? '1';
  const searchType = query?.get('type') as string;
  const getCurrentTab = () => {
    const tabIndex = tabs?.findIndex((x) =>
      searchType ? x.searchType === searchType : x.tabName.includes(searchTerm),
    );
    return tabIndex >= 0 ? tabIndex : 0;
  };
  const [currentTab, setCurrentTab] = useState(getCurrentTab);

  const seo = slotContent?.[0];

  const onSearch = useCallback((term: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('term', term);
    window.history.pushState(null, '', url);
  }, []);

  useEffect(() => {
    searchSubmitted({ query: searchTerm });
  }, [searchTerm]);
  useEffect(() => {
    setCurrentTab(getCurrentTab);
  }, [searchType]);

  return (
    <>
      {seo && <Seo {...seo} />}
      <Section gutterX gutterY className="tw-flex-1">
        <div className="tw-mt-2 md:tw-mt-10">
          <h1 className="tw-text-center tw-normal-case tra-text-web-body-xl">
            {title || 'Search products, recipes, or support information'}
          </h1>
        </div>

        <div id="instructions" className="sr-only">
          Begin typing to search, use arrow keys to navigate, Enter to select
        </div>

        <SearchAutocomplete
          searchTerm={searchTerm}
          placeholder={searchPlaceholder}
          className="tw-max-w-[35rem] tw-my-4 tw-mx-auto"
          onSearch={onSearch}
          aria-activedescendant={currentTab}
        />

        {tabs?.length > 0 && searchTerm && (
          <Tabs
            initialTab={currentTab}
            tabLabel={tabLabel || 'Select a search type'}
            tabHeadersClassName="tw-flex tw-justify-center tw-my-2"
            tabs={tabs.map((tab, index) => ({
              href: tab.redirectUrl ? `${tab.redirectUrl}${searchTerm}` : undefined,
              label: tab.tabName,
              content: (
                <SearchResultsList
                  {...tabs[index]}
                  page={page}
                  searchTerm={searchTerm}
                  searchType={tabs[index]?.searchType}
                  resultTextSingular={resultTextSingular}
                  resultTextPlural={resultTextPlural}
                />
              ),
            }))}
          />
        )}
      </Section>
    </>
  );
};

export default SearchResultsBody;
